import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getContacts, updatePipeline } from '../services/api';
import { timeAgo, pipelineLabel } from '../utils';

const STAGES = [
  { key: 'new_lead', label: 'New Lead', color: 'var(--blue)' },
  { key: 'contacted', label: 'Contacted', color: 'var(--cyan)' },
  { key: 'proposal_sent', label: 'Proposal Sent', color: 'var(--orange)' },
  { key: 'won', label: 'Won', color: 'var(--green)' },
  { key: 'lost', label: 'Lost', color: 'var(--text-muted)' },
];

export default function Pipeline() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    getContacts().then(res => setContacts(res.data.contacts || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const getStageContacts = (stage) =>
    contacts.filter(c => c.pipeline_stage === stage);

  const handleDragStart = (contact) => setDragging(contact);

  const handleDrop = async (stage) => {
    if (!dragging || dragging.pipeline_stage === stage) {
      setDragging(null);
      return;
    }
    try {
      await updatePipeline(dragging.id, stage);
      setContacts(prev => prev.map(c =>
        c.id === dragging.id ? { ...c, pipeline_stage: stage } : c
      ));
    } catch (e) {
      console.error(e);
    }
    setDragging(null);
  };

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div className="page-wrap" style={{ height: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Pipeline</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>
          Drag and drop contacts between stages
        </p>
      </div>

      {/* Kanban board */}
      <div style={{ flex: 1, overflowX: 'auto', overflowY: 'hidden' }} className="table-wrap">
        <div className="kanban-board" style={{ height: '100%' }}>
          {STAGES.map(stage => {
            const stageContacts = getStageContacts(stage.key);
            return (
              <div
                key={stage.key}
                onDragOver={e => e.preventDefault()}
                onDrop={() => handleDrop(stage.key)}
                className="kanban-col"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderTop: `3px solid ${stage.color}`,
                  borderRadius: 'var(--radius)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {/* Column header */}
                <div style={{
                  padding: '12px 14px',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: stage.color }}>
                    {stage.label}
                  </span>
                  <span style={{
                    background: 'var(--bg-hover)',
                    color: 'var(--text-muted)',
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 99,
                  }}>
                    {stageContacts.length}
                  </span>
                </div>

                {/* Cards */}
                <div style={{ flex: 1, overflow: 'auto', padding: '10px 10px' }}>
                  {stageContacts.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      color: 'var(--text-muted)',
                      fontSize: 12,
                      padding: '20px 0',
                    }}>
                      No contacts
                    </div>
                  ) : (
                    stageContacts.map(contact => (
                      <div
                        key={contact.id}
                        draggable
                        onDragStart={() => handleDragStart(contact)}
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border)',
                          borderRadius: 8,
                          padding: '12px',
                          marginBottom: 8,
                          cursor: 'grab',
                          transition: 'border-color 0.15s',
                          opacity: dragging?.id === contact.id ? 0.5 : 1,
                        }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = stage.color}
                        onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                      >
                        {/* Avatar + name */}
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                          <div style={{
                            width: 28, height: 28,
                            background: 'var(--bg-hover)',
                            borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, color: stage.color,
                            flexShrink: 0,
                          }}>
                            {(contact.name || contact.phone || '?')[0].toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {contact.name || 'Unknown'}
                            </div>
                            {contact.company && (
                              <div style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {contact.company}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Stats */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {contact.total_calls || 0} call{(contact.total_calls || 0) !== 1 ? 's' : ''}
                          </span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                            {timeAgo(contact.last_called_at)}
                          </span>
                        </div>

                        {/* Follow-up indicator */}
                        {contact.follow_up_date && new Date(contact.follow_up_date) < new Date() && (
                          <div style={{
                            marginTop: 8,
                            padding: '4px 8px',
                            background: 'rgba(239,68,68,0.1)',
                            border: '1px solid rgba(239,68,68,0.3)',
                            borderRadius: 4,
                            fontSize: 11,
                            color: 'var(--red)',
                          }}>
                            ⏰ Follow-up overdue
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
